import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Phone, MapPin, ShieldCheck, SlidersHorizontal, Building2,
  BadgeCheck, Wallet, HeartHandshake, Sparkles, CalendarClock,
} from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import QuoteForm from "@/components/QuoteForm";
import { ResultsGallery } from "@/components/ResultsGallery";
import { CtaSection } from "@/components/CtaSection";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { useSite } from "@/lib/SiteContext";
import { IMG, SERVICES, WHY_POINTS, BUDGET_SEGMENTS, STEPS, MARQUEE_ITEMS, isPlaceholder } from "@/lib/content";
import { REGIONS } from "@/lib/areas";
import { useSeo } from "@/hooks/useSeo";

const ICONS = { ShieldCheck, SlidersHorizontal, Building2, BadgeCheck, Wallet, HeartHandshake, Sparkles, CalendarClock };

export default function Home() {
  useSeo(
    "RD Cleaning Services | Professional Cleaning Services Perth WA",
    "Reliable residential and commercial cleaning services across Perth. House cleaning, office cleaning, end of lease, medical and industrial cleaning. Get a free quote today."
  );
  const { settings, areas } = useSite();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const phone = settings.phone;

  return (
    <>
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[92vh] items-center overflow-hidden bg-navy" data-testid="hero-section">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img
            src={IMG.hero}
            alt="Professional cleaner wiping down a bench in a bright, modern Perth home"
            className="h-[115%] w-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/40" />
        </motion.div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <Reveal y={14}>
            <p className="eyebrow !text-teal-300">Residential &amp; Commercial · Perth, WA</p>
          </Reveal>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-7xl">
            <MaskedLine delay={0.15}>Professional Cleaning</MaskedLine>
            <MaskedLine delay={0.32}>
              Services You Can <span className="text-teal-400">Trust</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
              Reliable residential and commercial cleaning services across Perth. From regular home cleaning
              to professional commercial and specialised cleaning, RD Cleaning Services helps keep your space
              clean, fresh and welcoming.
            </p>
          </Reveal>
          <Reveal delay={0.7}>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Link to="/quote" className="focus-ring inline-flex items-center justify-center gap-2 rounded-full bg-teal-500 px-8 py-4 text-base font-bold text-navy-deep shadow-xl shadow-teal-500/25 transition-all hover:-translate-y-0.5 hover:bg-teal-400" data-testid="hero-quote-button">
                Get a Free Quote <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/services" className="focus-ring inline-flex items-center justify-center rounded-full border border-white/30 px-8 py-4 text-base font-semibold text-white transition-colors hover:border-teal-300 hover:text-teal-300" data-testid="hero-services-button">
                Explore Our Services
              </Link>
              {!isPlaceholder(phone) ? (
                <a href={`tel:${phone.replace(/\s/g, "")}`} className="focus-ring inline-flex items-center justify-center gap-2 px-4 py-4 text-base font-semibold text-white transition-colors hover:text-teal-300" data-testid="hero-call-button">
                  <Phone className="h-5 w-5 text-teal-400" /> Call Us Today — {phone}
                </a>
              ) : (
                <Link to="/contact" className="focus-ring inline-flex items-center justify-center gap-2 px-4 py-4 text-base font-semibold text-white transition-colors hover:text-teal-300" data-testid="hero-call-button">
                  <Phone className="h-5 w-5 text-teal-400" /> Call Us Today
                </Link>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.85}>
            <p className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-slate-300" data-testid="hero-trust-statement">
              <span>Reliable Service</span>
              <span className="h-1 w-1 rounded-full bg-teal-400" />
              <span>Flexible Options</span>
              <span className="h-1 w-1 rounded-full bg-teal-400" />
              <span>Quality Cleaning</span>
            </p>
          </Reveal>
        </div>
        <div className="animate-float-soft absolute bottom-10 right-10 hidden max-w-xs rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-md lg:block">
          <p className="font-display text-sm font-semibold leading-snug text-white">
            Professional cleaning for every home and every business.
          </p>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* QUICK QUOTE */}
      <section id="quote" className="bg-white py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <Reveal className="lg:col-span-2 lg:sticky lg:top-28">
            <p className="eyebrow">Fast Response</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Tell us what needs cleaning.
            </h2>
            <p className="mt-5 leading-relaxed text-slate-600">
              Whether it's your family home, an office, a clinic or a warehouse — share a few details and
              we'll come back with a cleaning solution that fits your space, schedule and budget.
            </p>
            <ul className="mt-8 space-y-4">
              {["No obligation, free quotes", "One-off, regular or custom schedules", "Residential & commercial expertise"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                  <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-teal-600" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-3">
            <QuoteForm tid="home-quote" />
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-slate-50 py-20 sm:py-28" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Our Services</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Cleaning Services for Every Space
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              From everyday home cleaning to specialised commercial and industrial cleaning, we provide
              flexible solutions designed around your needs.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.08} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`service-card-${s.id}`}>
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={s.image} alt={`${s.name} in Perth`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent opacity-60" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-display text-lg font-bold tracking-tight text-navy">{s.name}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                    <div className="mt-5 flex items-center gap-4">
                      <Link to={s.link} className="focus-ring rounded text-sm font-semibold text-teal-700 underline-offset-4 hover:underline" data-testid={`service-learn-more-${s.id}`}>
                        Learn More
                      </Link>
                      <Link to="/quote" className="focus-ring ml-auto inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-teal-700" data-testid={`service-quote-${s.id}`}>
                        Get a Quote <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BUDGET SEGMENTS */}
      <section className="bg-navy py-20 sm:py-28" data-testid="budget-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="eyebrow !text-teal-300">For Every Budget</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Whatever your space, budget or requirements — we can help.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {BUDGET_SEGMENTS.map((b, i) => (
              <Reveal key={b.tag} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-7 backdrop-blur-sm transition-colors hover:border-teal-400/40 hover:bg-white/10" data-testid={`budget-card-${b.tag.toLowerCase()}`}>
                  <span className="inline-block rounded-full bg-teal-500/15 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-teal-300">{b.tag}</span>
                  <h3 className="mt-4 font-display text-lg font-bold text-white">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/quote" className="focus-ring inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-navy-deep transition-all hover:-translate-y-0.5 hover:bg-teal-400" data-testid="budget-quote-button">
              Get a Free Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section id="why-us" className="scroll-mt-20 bg-white py-20 sm:py-28" data-testid="why-us-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Why Us</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Why Choose RD Cleaning Services?
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_POINTS.map((w, i) => {
              const Icon = ICONS[w.icon];
              return (
                <Reveal key={w.title} delay={(i % 4) * 0.07}>
                  <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-teal-300 hover:shadow-lg" data-testid={`why-card-${i}`}>
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 font-display text-base font-bold text-navy">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* RESULTS GALLERY */}
      <section className="bg-slate-50 py-20 sm:py-28" data-testid="before-after-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="eyebrow">Results</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                See the Difference a Professional Clean Makes
              </h2>
              <p className="mt-4 text-slate-600">Each before image is shown beside its matching after image.</p>
            </div>
            <Link to="/quote" className="focus-ring inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-teal-700" data-testid="before-after-quote-button">
              Get Your Space Cleaned <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="mt-12">
            <ResultsGallery />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-20 sm:py-28" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Simple Process</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">How It Works</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.08}>
                <div className="relative h-full rounded-2xl border border-slate-200 bg-slate-50/60 p-7" data-testid={`step-card-${i}`}>
                  <span className="font-mono text-4xl font-bold text-teal-600/25">{s.num}</span>
                  <h3 className="mt-4 font-display text-base font-bold text-navy">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/quote" className="focus-ring inline-flex items-center gap-2 rounded-full bg-teal-600 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-teal-600/25 transition-all hover:-translate-y-0.5 hover:bg-teal-700" data-testid="steps-quote-button">
              Request a Free Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* COMMERCIAL SPOTLIGHT */}
      <section className="bg-navy py-20 sm:py-28" data-testid="commercial-spotlight">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="eyebrow !text-teal-300">For Business</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Professional Commercial Cleaning for Perth Businesses
            </h2>
            <p className="mt-5 leading-relaxed text-slate-300">
              A clean workplace creates a better environment for employees, customers and visitors.
              RD Cleaning Services provides flexible commercial cleaning solutions for offices, retail
              spaces, medical facilities, warehouses, education facilities and other commercial environments.
            </p>
            <Link to="/quote" className="focus-ring mt-8 inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-navy-deep transition-all hover:-translate-y-0.5 hover:bg-teal-400" data-testid="commercial-quote-button">
              Request a Commercial Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-3xl shadow-2xl">
              <img src={IMG.commercial} alt="Clean, modern commercial office space in Perth" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* RESIDENTIAL SPOTLIGHT */}
      <section className="bg-white py-20 sm:py-28" data-testid="residential-spotlight">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal className="order-2 lg:order-1">
            <div className="overflow-hidden rounded-3xl shadow-2xl">
              <img src={IMG.living} alt="Warm, freshly cleaned living room in a Perth home" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="order-1 lg:order-2">
            <p className="eyebrow">For Your Home</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              A Cleaner Home. More Time for You.
            </h2>
            <p className="mt-5 leading-relaxed text-slate-600">
              Whether you need a regular cleaner, a deep clean or help preparing your property for a move,
              RD Cleaning Services provides practical cleaning solutions for homes and apartments across Perth.
            </p>
            <Link to="/residential" className="focus-ring mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-teal-700" data-testid="residential-book-button">
              Book a Home Cleaning <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <TestimonialsSection />

      {/* SERVICE AREAS */}
      <section className="bg-white py-20 sm:py-28" data-testid="service-area-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Where We Work</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Serving Perth &amp; Surrounding Areas
            </h2>
            <p className="mt-4 text-slate-600">
              We service homes and businesses across the Perth metropolitan area. Not sure if we cover your
              suburb? Just ask — we're flexible.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {areas.map((a, i) => (
              <Reveal key={a._id || a.name} delay={(i % 3) * 0.07}>
                <div className="flex h-full items-start gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-6" data-testid={`area-card-${i}`}>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-bold text-navy">{a.name}</h3>
                    {a.suburbs && <p className="mt-1 text-sm text-slate-600">{a.suburbs}</p>}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="home-region-links">
              {REGIONS.map((r) => (
                <Link
                  key={r.slug}
                  to={`/areas/${r.slug}`}
                  className="focus-ring inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-teal-700"
                  data-testid={`home-region-link-${r.slug}`}
                >
                  {r.name} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ))}
              <Link to="/areas" className="focus-ring text-xs font-bold text-teal-700 underline-offset-4 hover:underline" data-testid="home-all-areas-link">
                View all service areas
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
